// components/MusicPanel.tsx
// [내 음악] 패널: 보드에 저장한 곡 목록(재생/정지/삭제), 볼륨, 그리고 mp3 를 USB 로 보드에 저장하는 곳.
// 파일은 서버를 거치지 않고 이 PC 에서 보드로 바로 저장된다. (상태와 명령은 useMusic.ts)
import React, { useState } from 'react';
import { MUSIC_MAX_SONGS, MUSIC_VOLUME_MAX, MusicState } from './useMusic';

const formatSize = (kb: number) => (kb >= 1024 ? `${(kb / 1024).toFixed(1)}MB` : `${kb}KB`);
const titleFromName = (name: string) => name.replace(/\.[^.]+$/, '').slice(0, 40);

const MusicPanel: React.FC<{ music: MusicState }> = ({ music }) => {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [fileError, setFileError] = useState('');

  const pickFile = (f: File | null) => {
    setFileError('');
    setFile(null);
    if (!f) return;
    if (!/\.mp3$/i.test(f.name)) { setFileError('mp3 파일만 저장할 수 있습니다.'); return; }
    if (f.size === 0) { setFileError('빈 파일입니다.'); return; }
    if (music.freeKb !== null && f.size > music.freeKb * 1024) {
      setFileError(`저장 공간이 부족합니다. (남은 공간 ${formatSize(music.freeKb)}, 이 파일 ${formatSize(Math.ceil(f.size / 1024))}) 곡을 삭제하거나 모노·낮은 비트레이트로 다시 만든 파일을 쓰세요.`);
      return;
    }
    setFile(f);
    setTitle(titleFromName(f.name));
  };

  const full = music.songs.length >= MUSIC_MAX_SONGS;
  const canUpload = !!file && !!title.trim() && !music.uploading && music.serialSupported && !full;

  const box = { marginTop: '10px', padding: '10px 14px', borderRadius: '12px', fontSize: '0.88rem' } as const;

  return (
    <div className="otto-subpanel">
      <div className="otto-subpanel-title">내 음악</div>

      {/* 곡 목록 */}
      {music.songs.length === 0 ? (
        <div style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '12px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span>
            {music.loaded
              ? '보드에 저장된 곡이 없습니다. 아래에서 mp3 를 저장하세요.'
              : '보드의 곡 목록을 불러오는 중입니다… (보드가 켜져 있고 연결돼 있어야 합니다)'}
          </span>
          {!music.loaded && (
            <button type="button" className="action-btn btn-secondary" onClick={music.refresh}>다시 불러오기</button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
          {music.songs.map((s) => {
            const isCur = music.playing && music.current === s.sn;
            return (
              <div key={s.sn} style={{
                display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                padding: '10px 12px', borderRadius: '12px',
                background: isCur ? '#eff6ff' : '#f8fafc', border: `1px solid ${isCur ? '#93c5fd' : '#e2e8f0'}`,
              }}>
                <span style={{ fontWeight: 700, color: '#475569', width: '20px' }}>{s.sn}</span>
                <span style={{ flex: '1 1 140px', minWidth: 0, fontWeight: 600, color: '#1e293b', overflowWrap: 'anywhere' }}>
                  {isCur ? '♪ ' : ''}{s.title}
                  <span style={{ marginLeft: '8px', fontWeight: 400, color: '#94a3b8', fontSize: '0.8rem' }}>{formatSize(s.sizeKb)}</span>
                </span>
                {isCur ? (
                  <button type="button" className="action-btn" onClick={music.stop}
                    style={{ backgroundColor: '#ef4444', color: '#ffffff', border: 'none' }}>
                    정지
                  </button>
                ) : (
                  <button type="button" className="action-btn btn-primary" onClick={() => music.play(s.sn)}>
                    재생
                  </button>
                )}
                <button type="button" className="action-btn btn-secondary"
                  onClick={() => { if (window.confirm(`"${s.title}" 곡을 보드에서 삭제할까요?`)) music.remove(s.sn); }}>
                  삭제
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* 볼륨 */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', background: '#f8fafc', padding: '10px 14px', borderRadius: '12px' }}>
        <label htmlFor="otto-music-volume" style={{ fontWeight: 600, color: '#334155', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>볼륨</label>
        <input
          id="otto-music-volume"
          type="range"
          min={0}
          max={MUSIC_VOLUME_MAX}
          step={1}
          value={music.volume}
          onChange={(e) => music.changeVolume(Number(e.target.value))}
          style={{ flex: 1, minWidth: 0 }}
        />
        <span style={{ width: '56px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: '#1e293b' }}>
          {music.volume} / {MUSIC_VOLUME_MAX}
        </span>
      </div>

      {/* 저장 */}
      <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid #e2e8f0' }}>
        <div style={{ fontWeight: 700, color: '#334155', marginBottom: '8px', fontSize: '0.92rem' }}>
          곡 저장 (USB){music.freeKb !== null ? ` · 남은 공간 ${formatSize(music.freeKb)}` : ''} · {music.songs.length}/{MUSIC_MAX_SONGS}곡
        </div>

        {!music.serialSupported && (
          <div style={{ ...box, marginTop: 0, marginBottom: '10px', background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e' }}>
            이 브라우저는 USB(COM 포트) 전송을 지원하지 않습니다. PC 의 Chrome 또는 Edge 에서 열어 주세요. (스마트폰은 저장할 수 없고, 저장된 곡을 듣는 것만 가능합니다)
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="file"
            accept=".mp3,audio/mpeg"
            disabled={music.uploading}
            onChange={(e) => pickFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
            style={{ flex: '1 1 220px', minWidth: 0, fontSize: '0.9rem' }}
          />
        </div>

        {file && (
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginTop: '10px' }}>
            <label htmlFor="otto-music-title" style={{ fontWeight: 600, color: '#334155', fontSize: '0.9rem' }}>제목</label>
            <input
              id="otto-music-title"
              type="text"
              value={title}
              maxLength={40}
              disabled={music.uploading}
              onChange={(e) => setTitle(e.target.value)}
              style={{
                flex: '1 1 180px', minWidth: 0, padding: '10px 12px',
                border: '1px solid #cbd5e1', borderRadius: '12px', background: '#f8fafc',
                fontSize: '0.9rem', color: '#1e293b', outline: 'none',
              }}
            />
            <button type="button" className="action-btn btn-primary" disabled={!canUpload}
              onClick={() => file && music.upload(file, title.trim())}>
              {music.uploading ? '저장 중…' : `USB 로 보드에 저장 (${formatSize(Math.ceil(file.size / 1024))})`}
            </button>
          </div>
        )}

        {full && (
          <div style={{ ...box, background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e' }}>
            저장할 수 있는 곡 수({MUSIC_MAX_SONGS}곡)가 가득 찼습니다. 곡을 삭제한 뒤 저장하세요.
          </div>
        )}

        {music.uploading && (
          <div style={{ marginTop: '10px' }}>
            <div style={{ height: '10px', borderRadius: '6px', background: '#e2e8f0', overflow: 'hidden' }}>
              <div style={{ width: `${music.progress}%`, height: '100%', background: '#3b82f6', transition: 'width 0.2s' }} />
            </div>
            <div style={{ marginTop: '6px', fontSize: '0.85rem', color: '#475569' }}>
              {music.progress}% — 저장하는 동안 USB 선을 빼거나 보드를 조작하지 마세요. (전송 속도가 느려 1MB 에 약 1.5분 걸립니다)
            </div>
          </div>
        )}
        {music.uploadStatus && (
          <div style={{ marginTop: '8px', fontSize: '0.88rem', color: '#2563eb', fontWeight: 500 }}>{music.uploadStatus}</div>
        )}
        {fileError && (
          <div style={{ ...box, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c' }}>{fileError}</div>
        )}

        <div style={{ marginTop: '12px', fontSize: '0.8rem', color: '#64748b', lineHeight: 1.6 }}>
          · 직접 만들었거나 사용 권리가 있는 mp3 만 저장해 주세요. 저작권은 사용자에게 책임이 있습니다.<br />
          · 파일은 서버를 거치지 않고 이 PC 에서 USB 로 보드에 바로 저장됩니다.<br />
          · mp3, <b>모노 64~128kbps</b> 를 권장합니다. (1분에 약 0.5~1MB. 다른 형식은 mp3 로 먼저 변환하세요)<br />
          · 저장이 끝나면 보드가 다시 시작할 수 있습니다. 잠시 뒤 목록에 곡이 나타납니다.
        </div>
      </div>

      {music.error && (
        <div style={{ ...box, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c' }}>{music.error}</div>
      )}
    </div>
  );
};

export default MusicPanel;
